import { Eth } from 'web3-eth'

import { logWarn } from '@/shared/logger'
import { SMART_CONTRACT_CONFIG } from '@/shared/SmartContractConfig'
import { Transaction, TransactionOptions } from '@/shared/models/Transaction'
import { GasProvider } from '@/shared/models/GasProvider'

//dont apply changes to these parameters when merging the final
//tranasction in the background
const SKIP_PARAMETERS: Set<string> = new Set(['force'])

/** Add a new change option into the transaction.options
 *
 * @param transaction - The transaction to attach options to
 * @param key - The property to update
 * @param value - the value
 */
function _addRequest(
  transaction: Transaction,
  key: keyof Transaction,
  value?: string | number | boolean
) {
  const options = (transaction.options = transaction.options || [])
  const oldIndex = options.findIndex(({ key: _key }) => key === _key)
  if (oldIndex !== -1) {
    options.splice(oldIndex, 1)
  }
  options.push({ key, value })
}

/** apply modifications (used inside of the background)
 *
 * @param transaction - The transaction to attach options to
 * @param args - Arguments definition as per abi
 * @param index - 1 based index of argument to set to value
 * @param value - The value of the force parameter (assumed to be third arg)
 */
function changeForce(
  eth: Eth,
  transaction: Transaction,
  arguments_: string[],
  index: number,
  value: boolean
) {
  if (!transaction.data) {
    return // To make typescript happy
  }
  if (index < 1 || index > arguments_.length) {
    throw new Error('Invalid argument index')
  }
  const parameters = eth.abi.decodeParameters(
    arguments_,
    transaction.data?.slice(10)
  )
  // Adjust force
  parameters[index - 1] = value
  // Map parameters to an array to make them compatible with encodeParameters (null not allowed)
  const newParameters = arguments_.map(
    (key, index) => parameters[`${index}`] ?? '0x'
  )
  // Get the encoded parameters skipping the 0x in the front
  const output = eth.abi.encodeParameters(arguments_, newParameters).slice(2)
  // Adjust the payload
  transaction.data = transaction.data?.slice(0, 10) + output
}

/** apply modifications (used inside of the background)
 *
 * @param transaction - The transaction to attach options to
 * @param key - The property to update
 * @param value - The value to set the property to
 */
export function applyModification(
  eth: Eth,
  transaction: Transaction,
  key: keyof Transaction,
  value?: any,
  skipRecalculate = false
) {
  if (skipRecalculate && SKIP_PARAMETERS.has(key)) {
    return
  }
  switch (key) {
    case 'force': {
      transaction.force = value as boolean
      const signature = transaction.data?.slice(0, 10) || ''
      if (
        signature in
        {
          [SMART_CONTRACT_CONFIG.lsp7Selector]: true,
          [SMART_CONTRACT_CONFIG.lsp8Selector]: true,
        }
      ) {
        changeForce(
          eth,
          transaction,
          signature === SMART_CONTRACT_CONFIG.lsp7Selector
            ? ['address', 'address', 'uint256', 'bool', 'bytes']
            : ['address', 'address', 'bytes32', 'bool', 'bytes'],
          4,
          value
        )
      }
      break
    }
    case 'maxPriorityFeePerGas': {
      transaction.maxPriorityFeePerGas = value
      break
    }
    case 'gasPrice': {
      transaction.gasPrice = value
      break
    }
    case 'gasLimit': {
      transaction.gasLimit = value
      break
    }
    case 'gasProvider': {
      transaction.gasProvider = value
      break
    }
    case 'components': {
      transaction.components = value
      break
    }
    case 'data': {
      transaction.data = value
      break
    }
    default: {
      logWarn(`Invalid modification of ${key} with ${value}`)
      break
    }
  }
}

/** request modifications (used inside of the popup)
 * this routine would attach those changes into a changelog on transaction.options
 *
 * @param transaction - The transaction to attach options to (this is a clone of the one in the backend)
 * @param key - The property to update
 * @param value - The value to set the property to
 * @returns Bool flag indicating whether the modification requires background validation
 */
export function requestModification(
  transaction: Transaction,
  key: string,
  value?: any
) {
  switch (key) {
    case 'force': {
      transaction.force = value as boolean
      _addRequest(transaction, key, value)
      return true
    }
    case 'maxPriorityFeePerGas': {
      transaction.maxPriorityFeePerGas = value
      _addRequest(transaction, key, value)
      return false
    }
    case 'gasPrice': {
      transaction.gasPrice = value as string
      _addRequest(transaction, key, value)
      return false
    }
    case 'to': {
      // View only so don't add modification for background
      transaction.to = value as string
      return false
    }
    case 'gasProvider': {
      transaction.gasProvider = value as GasProvider
      _addRequest(transaction, key, value)
      return true
    }
    default: {
      logWarn(`Invalid modification of ${key} with ${value}`)
      break
    }
  }
}

/**
 * Call applyModification for each items in the optional changelog
 *
 * @param transaction - The transaction to apply the modification to
 * @param options - The changelog to apply
 */
export function applyModifications(
  eth: Eth,
  transaction: Transaction,
  options?: TransactionOptions,
  skipRecalculate = false
) {
  if (options) {
    for (const { key, value } of options) {
      applyModification(eth, transaction, key, value, skipRecalculate)
    }
  }
}

/** Get final value of modifications for a particular property
 *
 * @param transaction - The transaction
 * @param key - which property to look for
 */
export function getModification(
  transaction: Transaction | TransactionOptions | undefined,
  key: 'force'
): boolean
export function getModification(
  transaction: Transaction | TransactionOptions | undefined,
  key: 'gasProvider'
): GasProvider
export function getModification(
  transaction: Transaction | TransactionOptions | undefined,
  key: 'relayerId' | 'to' | 'gasPrice' | 'maxPriorityFeePerGas'
): string
export function getModification(
  transaction: Transaction | TransactionOptions | undefined,
  key: string
) {
  const options: TransactionOptions =
    transaction == undefined
      ? []
      : Array.isArray(transaction)
      ? (transaction as TransactionOptions)
      : (transaction as Transaction).options || []
  const { value } = options.find(({ key: _key }) => key === _key) || {}
  return value
}
